import { ArrowUpFromLine, Check, ChevronsUpDown, Copy, Download, Sparkles, X } from "lucide-react";
import { Button } from "../ui/button.tsx";
import { Dialog, DialogContent, DialogTrigger } from "../ui/dialog.tsx";
import { UseMutateFunction } from "@tanstack/react-query";
import { SetStateAction, useEffect, useRef, useState } from "react";
import ImageLoader from "../ui/image-loader.tsx";
import CustomImageForm from "./CustomImageForm";
import { cn } from "@/lib/utils";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/shared/ui/command.tsx";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover.tsx";
import { Catalog } from "./api/single-post-api";
import { UseFormReturn } from "react-hook-form";
import { PostBodyFormType } from "./types";
import { useAtomValue } from "jotai";
import { themeAtom } from "@/features/app-layout/atoms/theme-atoms";

type Props = {
  post_id: number,
  imgUrl: string,
  isSuccess: boolean,
  onSavePost: () => void,
  image_recommendation: string,
  postForm: UseFormReturn<PostBodyFormType>,
  catalog: Catalog[],
  isLoading: boolean,
  setIsLoading: React.Dispatch<SetStateAction<boolean>>,
  generateImage: UseMutateFunction<{ status: string; data: { post_id: number; platform: string; text: string; image_video_recommendations: string; hashtags: string[]; cta: string; date_of_posting: string; time_of_posting: string; image_url: string; }; }, Error, { id: number, catalog_id: number }, unknown>;
}
const ImageSection = ({ post_id, imgUrl, image_recommendation, generateImage, isLoading, setIsLoading, onSavePost, catalog: catalogData, postForm, isSuccess }: Props) => {

  const isClicked = useRef(false)

  const [showImageForm, setShowImageForm] = useState(false)

  const theme = useAtomValue(themeAtom)

  const [image, setImage] = useState<string>(imgUrl)

  const [open, setOpen] = useState(false)

  const catValue = postForm.watch("catalogue")

  useEffect(() => {
    setImage(imgUrl)
  }, [imgUrl])

  useEffect(() => {
    if (isSuccess && isClicked.current) {
      console.log("regenerate image clicked")
      generateImage({ id: post_id, catalog_id: 2 })
      isClicked.current = false
    }
  }, [isSuccess, isClicked])

  return (
    <div className="w-full md:w-1/2 p-4 flex flex-col sm:flex-row md:flex-col gap-5">
      <div className="w-full h-fit flex flex-col gap-2">
        <Dialog>
          <DialogTrigger>
            {isLoading &&
              <ImageLoader />
            }
            {!showImageForm && !isLoading && imgUrl !== null && <img
              src={image}
              loading="lazy"
              className="w-2/3 sm:w-full h-fit rounded-lg cursor-pointer"
              alt="Preview"
              width="500px"
              height="500px"
            />}
          </DialogTrigger>
          <DialogContent className="max-w-2xl bg-black text-purple-500 font-semibold border-none outline-black p-0">
            <div className="absolute bottom-2 right-2 flex items-center gap-2">
              <Button size="icon"
                title="Download Image"
                className=""
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = imgUrl;
                  link.download = imgUrl.split('/').pop() || 'image';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}>
                <Download />
              </Button>
              <Button size="icon"
                onClick={() => {
                  navigator.clipboard.writeText(image_recommendation)
                }}
              >
                <Copy />
              </Button>
            </div>
            <img src={imgUrl !== null ? imgUrl : ""} className="w-full h-auto rounded-lg" alt="Full View" />
          </DialogContent>
        </Dialog>
        {showImageForm && (
          <CustomImageForm postId={post_id} currentUrl={imgUrl} setShowImageForm={setShowImageForm} setImage={setImage} />
        )}
        <div className="flex items-center gap-3 w-full">
          {showImageForm ? (
            <Button className="flex bg-theme-primary items-center gap-2 justify-center" onClick={() => setShowImageForm(false)}>
              <X />
              <span>Cancel</span>
            </Button>) : (
            <Button style={{ backgroundColor: theme.primary }} className="flex bg-theme-primary items-center gap-2 justify-center" onClick={() => setShowImageForm(true)}>
              <ArrowUpFromLine />
              <span>Upload</span>
            </Button>
          )}
          <Button style={{ backgroundColor: theme.primary }} className="flex items-center gap-2 justify-center"
            disabled={isLoading}
            onClick={() => {
              isClicked.current = true
              onSavePost()
              setIsLoading(true)
              // generateImage({ id: post_id, catalog_id: 2 })
            }}>
            <Sparkles />
            <span>Regenerate</span>
          </Button>
        </div>
        <p className="font-poppins font-semibold text-sm">Choose Catalog</p>
        <Popover open={open} onOpenChange={setOpen} modal>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              className="w-full justify-between overflow-hidden h-full"
            >
              {catValue
                ? catalogData.find(item => item.unique_identifier === catValue)?.name
                : "Select Product..."}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="p-0 md:w-[500px] lg:w-[800px]">
            <Command>
              <CommandInput placeholder="Search Product..." className="h-9" />
              <CommandList>
                <CommandEmpty>No Item found.</CommandEmpty>
                <CommandGroup>
                  {catalogData && catalogData.map((item) => (
                    <CommandItem
                      key={item.name}
                      value={item.name}
                      onSelect={() => {
                        postForm.setValue("catalogue", item.unique_identifier)
                        setOpen(false)
                      }}
                      className="flex items-center gap-2"
                    >
                      <img
                        src={item.image_url}
                        alt={item.name}
                        className="w-6 h-6 object-contain"
                      />
                      {item.name}
                      <Check
                        className={cn(
                          "ml-auto h-4 w-4",
                          catValue === item.unique_identifier ? "opacity-100" : "opacity-0"
                        )}
                      />
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  )
}

export default ImageSection;
